import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 0;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Listen for Escape key to close mega menu
    document.addEventListener('keydown', this.#handleEscapeKey, {
      signal: this.#abortController.signal,
    });

    // Listen for Enter key on arrow spans
    document.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Resolve the list item from various event targets
   * @param {PointerEvent | FocusEvent | KeyboardEvent} event
   * @returns {HTMLElement | null}
   */
  #resolveListItem(event) {
    if (!(event.target instanceof Element)) return null;

    // For keyboard events on arrows, find the parent list item
    if (event.type === 'keydown' && 'key' in event && event.key === 'Enter') {
      return /** @type {HTMLElement | null} */ (event.target.closest('.menu-list__list-item'));
    }

    // For pointer events, the target should already be the list item or child
    const listItem = event.target.matches('.menu-list__list-item')
      ? event.target
      : event.target.closest('.menu-list__list-item');
    
    return /** @type {HTMLElement | null} */ (listItem);
  }

  /**
   * Handle Enter key press on arrow spans to toggle menu
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof Element)) return;

    // Check if the target is an arrow span
    const isArrow = event.target.matches('.menu-list__link-arrow');
    if (!isArrow) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem) return;

    const menuItem = findMenuItem(listItem);
    if (!menuItem) return;

    event.preventDefault();

    // Toggle based on current state
    if (menuItem.ariaExpanded === 'true') {
      this.#debouncedActivateHandler.cancel();
      this.#debouncedDeactivate();
    } else {
      this.#debouncedDeactivate.cancel();
      this.#debouncedActivateHandler.cancel();
      // Call activate handler with a synthetic event object
      this.#activateHandler({ target: listItem, type: 'keydown' });
    }
  };


  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | KeyboardEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    const listItem = this.#resolveListItem(event);
    if (!listItem) return;

    this.#debouncedActivateHandler({ ...event, target: listItem });
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | KeyboardEvent | {target: Element, type: string}} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const target = event.target;
    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Update arrow span aria-expanded
      const previousArrow = previouslyActiveItem.parentElement?.querySelector('.menu-list__link-arrow');
      if (previousArrow) previousArrow.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    
    // Update arrow span aria-expanded
    const arrow = item.parentElement?.querySelector('.menu-list__link-arrow');
    if (arrow) arrow.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent | KeyboardEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    const listItem = this.#resolveListItem(event);
    if (!listItem) return;

    const item = findMenuItem(listItem);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate immediately when entering a menu item without sublinks
   */
  deactivateNoLinks = () => {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    
    // Update arrow span aria-expanded
    const arrow = item.parentElement?.querySelector('.menu-list__link-arrow');
    if (arrow) arrow.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle Escape key press to close mega menu
   * @param {KeyboardEvent} event
   */
  #handleEscapeKey = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#state.activeItem) return;

    // Cancel any pending activations/deactivations
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    // Store reference to active item before deactivating
    const activeItem = this.#state.activeItem;
    const currentListItem = activeItem.closest('.menu-list__list-item');

    // Immediately deactivate the menu
    this.#deactivate(activeItem);

    // Find the next top-level menu item to focus
    if (currentListItem) {
      const nextListItem = currentListItem.nextElementSibling;
      const nextMenuLink = nextListItem?.querySelector('[ref="menuitem"]');

      // Use setTimeout to ensure focus happens after deactivation completes
      setTimeout(() => {
        if (nextMenuLink instanceof HTMLElement) {
          nextMenuLink.focus({ preventScroll: true });
        }
      }, 0);
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
